import React from 'react';
import type { ProviderFeeOperation } from '../../../types';
import Input from '../../ui/Input';
import type { EditableRow, NetworkFeeType, SupportedByKeyMap } from './providerFeesTypes';
import {
  normalizeSupportedMethodId,
  prettyMethodLabel,
  toPercentString,
  toRateNumber,
  toSupportedKey,
} from './providerFeesUtils';

const OPERATIONS: ProviderFeeOperation[] = ['deposit', 'withdrawal', 'transfer'];
const NETWORK_FEE_TYPES: { value: NetworkFeeType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'percent', label: 'Percent (%)' },
  { value: 'fixed', label: 'Fixed (USD)' },
];

export default function ProviderFeeFormFields(props: {
  draftRow: EditableRow;
  setDraftRow: React.Dispatch<React.SetStateAction<EditableRow>>;
  providerOptions: string[];
  supportedByKey: SupportedByKeyMap;
  ensureSupportedMethods: (providerRaw: string, operationRaw: ProviderFeeOperation) => Promise<void>;
  lockScopeFields?: boolean;
}) {
  const { draftRow, setDraftRow, providerOptions, supportedByKey, ensureSupportedMethods, lockScopeFields = false } = props;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium">Provider</label>
          <select
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={draftRow.provider}
            onChange={async (e) => {
              const provider = e.target.value;
              const next = { ...draftRow, provider, methodId: 'all' };
              setDraftRow(next);
              await ensureSupportedMethods(provider, next.operation);
            }}
            disabled={lockScopeFields}
          >
            <option value="" disabled>
              Select provider…
            </option>
            {providerOptions.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Operation</label>
          <select
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={draftRow.operation}
            onChange={async (e) => {
              const op = e.target.value as ProviderFeeOperation;
              const next: EditableRow = {
                ...draftRow,
                operation: op,
                methodId: 'all',
              };
              setDraftRow(next);
              await ensureSupportedMethods(next.provider, next.operation);
            }}
            disabled={lockScopeFields}
          >
            {OPERATIONS.map((op) => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Coin / Network</label>
        <select
          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={draftRow.methodId || 'all'}
          onChange={(e) => setDraftRow((prev) => ({ ...prev, methodId: e.target.value }))}
          disabled={lockScopeFields || !draftRow.provider || draftRow.operation === 'transfer'}
          title={
            lockScopeFields
              ? 'Method cannot be changed in edit mode'
              : !draftRow.provider
              ? 'Select provider first'
              : draftRow.operation === 'transfer'
                ? 'Not applicable for transfer'
                : ''
          }
        >
          <option value="all">All</option>
          {(() => {
            const key = toSupportedKey(
              (draftRow.provider || '').trim().toLowerCase(),
              String(draftRow.operation || '').trim().toLowerCase(),
            );
            const methods = supportedByKey[key] || [];
            return methods
              .map((m) => ({ id: normalizeSupportedMethodId(m), label: prettyMethodLabel(m) }))
              .filter((m) => m.id)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ));
          })()}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Gateway processing fee (%)"
          type="number"
          inputMode="decimal"
          value={draftRow.paymentGateFeePercent}
          onChange={(e) => setDraftRow((prev) => ({ ...prev, paymentGateFeePercent: e.target.value }))}
          placeholder="0"
          min={0}
          step="0.01"
        />
        <Input
          label="System fee (%)"
          type="number"
          inputMode="decimal"
          value={draftRow.systemFeePercent}
          onChange={(e) => setDraftRow((prev) => ({ ...prev, systemFeePercent: e.target.value }))}
          placeholder="0"
          min={0}
          step="0.01"
        />
      </div>

      {(() => {
        const isZeroxLiveNetworkFee =
          (draftRow.provider || '').trim().toLowerCase() === 'zeroxprocessing' &&
          (draftRow.methodId || '').trim().toLowerCase().startsWith('zerox_');
        if (isZeroxLiveNetworkFee) {
          return (
            <div className="space-y-2 rounded-md border border-border bg-muted/30 p-4">
              <p className="text-sm font-medium">
                Network fee{' '}
                <span className="ml-1 rounded-sm bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                  live from 0xprocessing
                </span>
              </p>
              <p className="text-sm tabular-nums">
                {draftRow.networkFeeType === 'fixed'
                  ? `$${draftRow.networkFeeFixedUsd || '0'}`
                  : draftRow.networkFeeType === 'percent'
                    ? `${draftRow.networkFeePercent || '0'}%`
                    : '—'}
              </p>
              <p className="text-xs text-muted-foreground">
                Synced from the 0xprocessing Coins API — NetworkFee for deposits, MinimumWithdrawFee for
                withdrawals. Not editable; the exact live value is applied to every transaction.
              </p>
            </div>
          );
        }
        return (
          <div className="space-y-3 rounded-md border border-border p-4">
            <p className="text-sm font-medium">Network fee</p>
            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={draftRow.networkFeeType}
                onChange={(e) =>
                  setDraftRow((prev) => ({
                    ...prev,
                    networkFeeType: e.target.value as NetworkFeeType,
                  }))
                }
              >
                {NETWORK_FEE_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {draftRow.networkFeeType === 'percent' ? (
              <Input
                label="Network fee (%)"
                type="number"
                inputMode="decimal"
                value={draftRow.networkFeePercent}
                onChange={(e) => setDraftRow((prev) => ({ ...prev, networkFeePercent: e.target.value }))}
                placeholder="0"
                min={0}
                step="0.01"
              />
            ) : null}
            {draftRow.networkFeeType === 'fixed' ? (
              <Input
                label="Network fee (USD)"
                type="number"
                inputMode="decimal"
                value={draftRow.networkFeeFixedUsd}
                onChange={(e) => setDraftRow((prev) => ({ ...prev, networkFeeFixedUsd: e.target.value }))}
                placeholder="0"
                min={0}
                step="0.01"
              />
            ) : null}
          </div>
        );
      })()}

      <p className="text-sm text-muted-foreground">
        Total fee (%):{' '}
        <span className="font-medium text-foreground tabular-nums">
          {toPercentString(
            toRateNumber(draftRow.paymentGateFeePercent) + toRateNumber(draftRow.systemFeePercent)
          )}
        </span>
        {' — '}
        gateway processing + system. Network fee is configured separately.
      </p>
    </div>
  );
}
